import React, { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as DollarSvg } from '@assets/svg/dashboard/dashboard_dollar-circle.svg';
import { ReactComponent as EquipmentSvg } from '@assets/svg/dashboard/dashboard_pile.svg';
import { ReactComponent as ThunderBoltSvg } from '@assets/svg/dashboard/dashboard_thunderbolt.svg';
import { CategoryButtonTitle } from '@models/home';
import { Button } from 'antd';

import './CategoryTogglingButtonList.scss';

interface ICategoryTogglingButtonProps {
  icon: React.ReactNode;
  title: string;
  iconAreaBgColor: string;
  buttonBgColor: string;
  textColor: string;
  onClick: () => void;
  style: React.CSSProperties;
}

function CategoryTogglingButton({
  icon,
  title,
  iconAreaBgColor,
  buttonBgColor,
  textColor,
  onClick,
  style,
}: ICategoryTogglingButtonProps) {
  return (
    <Button
      className="CategoryTogglingButton"
      style={{ ...style, backgroundColor: buttonBgColor }}
      onClick={() => onClick()}
    >
      <div
        className="CategoryTogglingButton__icon-area"
        style={{ backgroundColor: iconAreaBgColor }}
      >
        {icon}
      </div>
      <h4
        className="CategoryTogglingButton__title"
        style={{ color: textColor }}
      >
        {title}
      </h4>
    </Button>
  );
}

function CategoryTogglingButtonList({
  setActiveCategory,
}: {
  setActiveCategory: React.Dispatch<
    React.SetStateAction<CategoryButtonTitle | null>
  >;
}) {
  const { t } = useTranslation();
  const [activeKey, setActiveKey] = useState<CategoryButtonTitle>(null);

  function handleTabButtonClick(title: CategoryButtonTitle) {
    if (title === activeKey) {
      setActiveKey(null);
      setActiveCategory(null);
      return;
    }
    setActiveKey(title);
    setActiveCategory(title);
  }

  const categoryButtonList: {
    icon: ReactNode;
    title: string;
    key: CategoryButtonTitle;
  }[] = [
    {
      icon: <ThunderBoltSvg />,
      title: t('CategoryTogglingButtonList.EnergyDispatchSecurity'),
      key: 'Home.EnergyDispatchSecurity',
    },
    {
      icon: <EquipmentSvg />,
      title: t('CategoryTogglingButtonList.ChargingEquipmentStability'),
      key: 'Home.ChargingEquipmentStability',
    },
    {
      icon: <DollarSvg />,
      title: t('CategoryTogglingButtonList.FinancialProfitabilityEfficiency'),
      key: 'Home.FinancialProfitabilityEfficiency',
    },
  ];

  return (
    <div className="CategoryTogglingButtonList">
      {categoryButtonList.map((tabButton, idx: number) => (
        <CategoryTogglingButton
          key={tabButton.title}
          icon={tabButton.icon}
          title={tabButton.title}
          iconAreaBgColor={activeKey !== tabButton.key ? '#F1F8FF' : '#FFF'}
          buttonBgColor={activeKey !== tabButton.key ? '#FFF' : '#2F3E73'}
          textColor={activeKey !== tabButton.key ? '#2F3E73' : '#FFF'}
          style={{ animationDelay: `${idx * 0.5 + (idx / 10) * 1.2}s` }}
          onClick={() => handleTabButtonClick(tabButton.key)}
        />
      ))}
    </div>
  );
}

export default CategoryTogglingButtonList;
